import { useState, type JSX } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { AudioStrip, Id, MonitoringMode } from '../../../core/model';
import { Dialog } from '../../components/Dialog';
import { NumberField } from '../../components/NumberField';
import { Slider } from '../../components/Slider';
import { useProject, useStudio } from '../../context';
import { useEditSession } from '../../hooks/useEditSession';
import { attempt } from '../../safe';
import { dbToGain, FADER_MAX_DB, gainToDb } from './fader';
import { useAudioSourceIds } from './MixerPanel';
import { MONITORING_LABELS } from './Strip';

type Unit = 'db' | 'percent';

function Row({ sourceId, unit }: { sourceId: Id; unit: Unit }): JSX.Element | null {
  const studio = useStudio();
  const row = useProject(useShallow((p) => (p.sources[sourceId] ? { name: p.sources[sourceId].name, ...p.sources[sourceId].audio } : null)));
  const session = useEditSession(studio);
  if (!row) return null;
  /** With `live`, the change belongs to a typing or dragging burst that `seal` records as one undo step. */
  const set = (patch: Partial<AudioStrip>, live = false): void => {
    if (!live) {
      session.commit();
      attempt(studio, () => studio.commands.setAudioStrip(sourceId, patch));
      return;
    }
    for (const key of Object.keys(patch) as (keyof AudioStrip)[]) {
      session.edit(
        key,
        () => studio.project.getState().project.sources[sourceId]?.audio[key],
        (next, options) => studio.commands.setAudioStrip(sourceId, { [key]: next }, options),
        patch[key],
      );
    }
  };
  const seal = (): void => session.commit();
  const db = gainToDb(row.volume);

  return (
    <tr>
      <th scope="row" className="ui-ellipsis" style={{ maxWidth: 180 }}>
        {row.name}
      </th>
      <td>
        {unit === 'db' ? (
          <NumberField
            label={`${row.name} volume in dB`}
            value={db === -Infinity ? -100 : Number(db.toFixed(1))}
            min={-100}
            max={FADER_MAX_DB}
            step={0.5}
            unit="dB"
            precision={1}
            onChange={(value) => set({ volume: value <= -100 ? 0 : dbToGain(value) }, true)}
            onCommit={seal}
          />
        ) : (
          <NumberField
            label={`${row.name} volume in percent`}
            value={Math.round(row.volume * 100)}
            min={0}
            max={Math.round(dbToGain(FADER_MAX_DB) * 100)}
            step={1}
            unit="%"
            precision={0}
            onChange={(value) => set({ volume: value / 100 }, true)}
            onCommit={seal}
          />
        )}
      </td>
      <td>
        <label className="ui-check">
          <input type="checkbox" checked={row.forceMono} aria-label={`${row.name} mono`} onChange={(e) => set({ forceMono: e.target.checked })} />
        </label>
      </td>
      <td style={{ minWidth: 150 }}>
        <div className="ui-row">
          <span className="ui-dim">L</span>
          <Slider
            label={`${row.name} balance`}
            value={row.balance}
            min={-1}
            max={1}
            step={0.01}
            marks={[0]}
            valueText={row.balance === 0 ? 'Centre' : `${Math.round(Math.abs(row.balance) * 100)}% ${row.balance < 0 ? 'left' : 'right'}`}
            onChange={(value) => set({ balance: value }, true)}
            onCommit={seal}
            onReset={() => set({ balance: 0 })}
          />
          <span className="ui-dim">R</span>
        </div>
      </td>
      <td>
        <NumberField
          label={`${row.name} sync offset in milliseconds`}
          value={row.syncOffsetMs}
          min={0}
          max={20000}
          step={10}
          unit="ms"
          precision={0}
          onChange={(value) => set({ syncOffsetMs: value }, true)}
          onCommit={seal}
        />
      </td>
      <td>
        <select
          className="ui-select"
          aria-label={`${row.name} monitoring`}
          value={row.monitoring}
          onChange={(e) => set({ monitoring: e.target.value as MonitoringMode })}
        >
          {(Object.keys(MONITORING_LABELS) as MonitoringMode[]).map((mode) => (
            <option key={mode} value={mode}>
              {MONITORING_LABELS[mode]}
            </option>
          ))}
        </select>
      </td>
    </tr>
  );
}

export function AdvancedAudioDialog({ onClose }: { onClose(): void }): JSX.Element {
  const ids = useAudioSourceIds();
  const [unit, setUnit] = useState<Unit>('db');
  return (
    <Dialog
      title="Advanced audio properties"
      onClose={onClose}
      width={920}
      footer={
        <>
          <label className="ui-check" style={{ marginRight: 'auto' }}>
            <input type="checkbox" checked={unit === 'percent'} onChange={(e) => setUnit(e.target.checked ? 'percent' : 'db')} />
            Show volume as a percentage
          </label>
          <button type="button" className="ui-btn primary" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      {ids.length === 0 ? (
        <div className="ui-empty">No audio sources.</div>
      ) : (
        <table className="ui-table">
          <thead>
            <tr>
              <th scope="col">Source</th>
              <th scope="col">Volume</th>
              <th scope="col">Mono</th>
              <th scope="col">Balance</th>
              <th scope="col">Sync offset</th>
              <th scope="col">Monitoring</th>
            </tr>
          </thead>
          <tbody>
            {ids.map((id) => (
              <Row key={id} sourceId={id} unit={unit} />
            ))}
          </tbody>
        </table>
      )}
      <p className="ui-dim">Sync offset delays audio only; delaying video (negative offsets) is not available in the browser.</p>
    </Dialog>
  );
}
